/**
 * Experience Summary Service
 *
 * Fetch API implementation of the curriculum reports service for Experience Summary.
 * Migrated from: assets/js/services/reporting/curriculumReports.js
 */

const API_SERVER = process.env.API_SERVER || '';

export interface ExperienceSummaryResponse {
  report_kind: string;
  report_key: string;
  collection_uuids: string[];
  data: ExperienceSummaryRecord[];
  [key: string]: unknown;
}

export interface ExperienceSummaryRecord {
  experience_id: number;
  experience_name: string;
  class_id: number;
  class_name: string;
  school_id: number;
  school_name: string;
  teacher_user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  ends_at: string;
  students_started: number;
  avg_progress: number;
  avg_responses_of_started: number;
  avg_quiz_score_of_ended: number | null;
  time_on_task: number;
  sum_response_count: number;
  unique_students: string[];
  archived_info: boolean;
  collection_uuid: string;
  collection: string;
  set_order: number;
  [key: string]: unknown;
}

export interface ExperienceSummaryFilter {
  include_archived?: boolean;
  year?: number;
  course?: string;
}

/**
 * Fetches experience summary report data for a specific report key.
 * @param reportKey - The key identifying the report
 * @param filter - Optional filter parameters (year, archived, course)
 * @returns Promise resolving to the report data, or empty array if no reportKey
 */
export async function getReportData(
  reportKey: string | null | undefined,
  filter?: ExperienceSummaryFilter
): Promise<ExperienceSummaryResponse | []> {
  if (!reportKey) {
    return [];
  }

  const params = new URLSearchParams();
  if (filter) {
    params.set('filter', JSON.stringify(filter));
  }

  const queryString = params.toString();
  const url = `${API_SERVER}/reports/curriculum/reports/${encodeURIComponent(reportKey)}${
    queryString ? `?${queryString}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch report data: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

const ExperienceSummaryService = {
  getReportData,
};

export default ExperienceSummaryService;
